import { defineStore } from 'pinia'
import { ref } from 'vue'
import { notify } from '@nimling/components-nimtech'
import type { Page, PageExport, PageGap, PageGridMode, PageLayout, ViewLayout } from '@/types'
import * as api from '@/lib/api'
import { useViewsStore } from '@/stores/views'

const GRID_MODES: PageGridMode[] = ['dynamic', 'fixed', 'single']
const GAPS: PageGap[] = ['none', 'tight', 'roomy']
const HOME_VIEWS: { template_id: string; layout: ViewLayout }[] = [
  { template_id: '00000000-0000-0000-0000-000000000229', layout: { x: 0, y: 0, w: 4, h: 7 } },
  { template_id: '00000000-0000-0000-0000-000000000247', layout: { x: 4, y: 0, w: 4, h: 7 } },
  { template_id: '00000000-0000-0000-0000-000000000231', layout: { x: 8, y: 0, w: 4, h: 7 } },
  { template_id: '00000000-0000-0000-0000-000000000232', layout: { x: 0, y: 7, w: 7, h: 9 } },
  { template_id: '00000000-0000-0000-0000-000000000246', layout: { x: 7, y: 7, w: 5, h: 9 } },
  { template_id: '00000000-0000-0000-0000-000000000245', layout: { x: 0, y: 16, w: 4, h: 8 } },
  { template_id: '00000000-0000-0000-0000-000000000248', layout: { x: 4, y: 16, w: 5, h: 8 } },
  { template_id: '00000000-0000-0000-0000-000000000249', layout: { x: 9, y: 16, w: 3, h: 8 } },
  { template_id: '00000000-0000-0000-0000-000000000251', layout: { x: 0, y: 24, w: 4, h: 7 } },
  { template_id: '00000000-0000-0000-0000-000000000250', layout: { x: 4, y: 24, w: 4, h: 7 } },
  { template_id: '00000000-0000-0000-0000-000000000252', layout: { x: 8, y: 24, w: 4, h: 7 } },
]

export const getPageLayout = (page: Page | null | undefined): PageLayout => {
  const raw = page?.layout as Partial<PageLayout> | null | undefined
  const mode = raw?.mode && GRID_MODES.includes(raw.mode) ? raw.mode : 'dynamic'
  const columns = Math.min(24, Math.max(1, Number(raw?.columns) || 12))
  const rows = Math.min(48, Math.max(1, Number(raw?.rows) || 12))
  const gap = raw?.gap && GAPS.includes(raw.gap) ? raw.gap : 'roomy'
  return { mode, columns, rows, gap, scroll_x: mode === 'dynamic' && raw?.scroll_x === true, bare: raw?.bare === true }
}

export const usePagesStore = defineStore('pages', () => {
  const isLoading = ref(false)
  const loaded = ref(false)
  const pages = ref<Page[]>([])
  const currentPage = ref<Page | null>(null)
  const deniedPage = ref<string | null>(null)

  const patchPage = (id: string, patch: (s: Page) => Page) => {
    pages.value = pages.value.map(s => (s.id === id ? patch(s) : s))
    if (currentPage.value?.id === id) currentPage.value = patch(currentPage.value)
  }

  const getPages = async (): Promise<Page[]> => {
    isLoading.value = true
    try {
      const rows = await api.getPages()
      pages.value = rows
      loaded.value = true
      return rows
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to load pages')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const getPage = async (id: string): Promise<Page | null> => {
    deniedPage.value = null
    const found = pages.value.find(s => s.id === id)
    if (found) {
      currentPage.value = found
      return found
    }
    try {
      const fetched = await api.getPage(id)
      currentPage.value = fetched
      return fetched
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 403) {
        deniedPage.value = id
        return null
      }
      notify('error', err instanceof Error ? err.message : 'Failed to load page')
      return null
    }
  }

  const postPage = async (input: Partial<Page>): Promise<Page> => {
    const created = await api.postPage(input)
    pages.value = [...pages.value, created]
    notify('success', `Page "${created.name}" created`)
    return created
  }

  const postHomePage = async (): Promise<Page> => {
    const page = await api.postPage({ name: 'Home', description: 'The stack at a glance: traffic, errors, latency, ingest, runtime, storage, uptime and alerts' })
    const viewsStore = useViewsStore()
    const views = await Promise.all(HOME_VIEWS.map(v => api.postViewCopy({ template_id: v.template_id, owner_id: page.owner_id })))
    await Promise.all(views.map((view, i) => viewsStore.postPageView(page.id, { view_id: view.id, layout: HOME_VIEWS[i].layout, status: 'active' })))
    pages.value = [...pages.value, page]
    return page
  }

  const putPage = async (s: Page): Promise<Page> => {
    const updated = await api.putPage(s)
    patchPage(updated.id, () => updated)
    return updated
  }

  const deletePage = async (id: string): Promise<void> => {
    await api.deletePage(id)
    const parentId = pages.value.find(x => x.id === id)?.parent_id ?? null
    pages.value = pages.value.filter(x => x.id !== id).map(x => (x.parent_id === id ? { ...x, parent_id: parentId } : x))
    if (currentPage.value?.id === id) currentPage.value = null
    notify('info', 'Page removed')
  }

  const getPageExport = async (id: string): Promise<void> => {
    const doc = await api.getPageExport(id)
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${doc.page.slug}.hvergel.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const postPageImport = async (doc: PageExport): Promise<Page> => {
    const res = await api.postPageImport(doc)
    const exists = pages.value.some(s => s.id === res.page.id)
    pages.value = exists
      ? pages.value.map(s => (s.id === res.page.id ? res.page : s))
      : [...pages.value, res.page]
    if (currentPage.value?.id === res.page.id) currentPage.value = res.page
    notify('success', `Imported "${res.page.name}": ${res.sources} source${res.sources === 1 ? '' : 's'}, ${res.views} view${res.views === 1 ? '' : 's'}, ${res.placements} placement${res.placements === 1 ? '' : 's'}`)
    return res.page
  }

  return {
    isLoading, loaded, pages, currentPage, deniedPage,
    getPages, getPage, postPage, postHomePage, putPage, deletePage,
    getPageExport, postPageImport
  }
})
